import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";

interface Survey {
  id: number;
  name: string;
  is_active: number;
}

interface SurveyQuestion {
  id: number;
  survey_id: number;
  question_text: string;
  question_type: string;
  sort_order: number;
  options: string | null;
}

interface SurveyWithQuestions extends Survey {
  questions: SurveyQuestion[];
}

interface SurveyRunnerProps {
  personId: string;
}

export function SurveyRunner({ personId }: SurveyRunnerProps) {
  const queryClient = useQueryClient();
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const { data: surveysData } = useQuery<{ data: Survey[] }>({
    queryKey: ["surveys"],
    queryFn: () => api.get<{ data: Survey[] }>("/admin/surveys"),
  });

  const { data: surveyDetail } = useQuery<{ data: SurveyWithQuestions }>({
    queryKey: ["survey", selectedSurveyId],
    queryFn: () => api.get<{ data: SurveyWithQuestions }>(`/admin/surveys/${selectedSurveyId}`),
    enabled: !!selectedSurveyId,
  });

  const submitMutation = useMutation({
    mutationFn: (body: { responses: { questionId: number; responseValue?: string }[] }) =>
      api.post(`/clients/${personId}/surveys/${selectedSurveyId}/responses`, body),
    onSuccess: () => {
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ["surveyResponses", personId] });
    },
  });

  const surveys = (surveysData?.data ?? []).filter((s) => s.is_active);
  const questions = surveyDetail?.data?.questions ?? [];

  if (submitted) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="mx-auto h-10 w-10 text-green-500" />
          <p className="mt-2 text-sm font-medium">Survey submitted successfully!</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-4"
            onClick={() => {
              setSubmitted(false);
              setSelectedSurveyId(null);
              setAnswers({});
            }}
          >
            Take Another Survey
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!selectedSurveyId) {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-semibold">Select a Survey</h4>
        {surveys.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">No active surveys available.</p>
            </CardContent>
          </Card>
        )}
        {surveys.map((s) => (
          <Card key={s.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setSelectedSurveyId(s.id)}>
            <CardHeader>
              <CardTitle className="text-sm">{s.name}</CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{surveyDetail?.data?.name ?? "Survey"}</h4>
        <Button variant="outline" size="sm" onClick={() => { setSelectedSurveyId(null); setAnswers({}); }}>
          Back
        </Button>
      </div>

      {questions.map((q, idx) => {
        let opts: string[] = [];
        if (q.options) {
          try {
            const parsed = JSON.parse(q.options);
            opts = Array.isArray(parsed) ? parsed : [];
          } catch { /* ignore */ }
        }

        return (
          <Card key={q.id}>
            <CardContent className="p-4 space-y-2">
              <p className="text-sm font-medium">{idx + 1}. {q.question_text}</p>
              {q.question_type === "text" && (
                <Input
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  placeholder="Your answer..."
                />
              )}
              {q.question_type === "textarea" && (
                <textarea
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Your answer..."
                />
              )}
              {q.question_type === "number" && (
                <Input
                  type="number"
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  placeholder="0"
                />
              )}
              {q.question_type === "date" && (
                <Input
                  type="date"
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                />
              )}
              {q.question_type === "yes_no" && (
                <div className="flex gap-4">
                  {["Yes", "No"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={`q-${q.id}`}
                        value={opt}
                        checked={answers[q.id] === opt}
                        onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt }))}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
              {q.question_type === "select" && opts.length > 0 && (
                <select
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select...</option>
                  {opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              )}
              {q.question_type === "scale" && (
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: String(n) }))}
                      className={`h-9 w-9 rounded-md border text-sm font-medium transition-colors ${
                        answers[q.id] === String(n) ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {questions.length > 0 && (
        <Button
          onClick={() => {
            const responses = questions.map((q) => ({
              questionId: q.id,
              responseValue: answers[q.id] ?? undefined,
            }));
            submitMutation.mutate({ responses });
          }}
          loading={submitMutation.isPending}
        >
          Submit Survey
        </Button>
      )}
    </div>
  );
}
